/**
 * Ordered action sequence builder — the SOAR vertical timeline.
 * Manages the list of actions: add / remove / reorder via [↑][↓].
 * Delegates each step's config to ActionRow.
 */
import { Button } from '@vnetwork/ui/primitives';
import { Plus } from 'lucide-react';
import { ActionRow } from './ActionRow';
import { defaultConfig, ACTION_TYPE_OPTIONS } from './action-type-meta';
import type { PlaybookAction, Channel, Watchpoint } from '@/api/healthcheck';

interface Props {
  actions: PlaybookAction[];
  channels: Channel[];
  watchpoints: Watchpoint[];
  showErrors: boolean;
  onChange: (actions: PlaybookAction[]) => void;
}

export function ActionBuilder({ actions, channels, watchpoints, showErrors, onChange }: Props) {
  const addAction = () => {
    const type = ACTION_TYPE_OPTIONS[0];
    onChange([
      ...actions,
      { type, config: defaultConfig(type), delay: actions.length === 0 ? 0 : 300 },
    ]);
  };

  const updateAt = (i: number, updated: PlaybookAction) => {
    const next = [...actions];
    next[i] = updated;
    onChange(next);
  };

  const removeAt = (i: number) => {
    onChange(actions.filter((_, idx) => idx !== i));
  };

  const moveUp = (i: number) => {
    if (i === 0) return;
    const next = [...actions];
    [next[i - 1], next[i]] = [next[i], next[i - 1]];
    onChange(next);
  };

  const moveDown = (i: number) => {
    if (i === actions.length - 1) return;
    const next = [...actions];
    [next[i], next[i + 1]] = [next[i + 1], next[i]];
    onChange(next);
  };

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
      {/* Vertical spine with numbered nodes */}
      {actions.map((action, i) => (
        <div key={i} style={{ display: 'flex', gap: 'var(--vn-space-3)' }}>
          {/* Spine */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              minWidth: 24,
              paddingTop: 2,
            }}
          >
            {/* Node dot */}
            <div
              style={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                background: 'var(--vn-accent-solid)',
                border: '2px solid var(--vn-border-strong)',
                flexShrink: 0,
              }}
            />
            {/* Connector line to next step */}
            {i < actions.length - 1 && (
              <div
                style={{
                  flex: 1,
                  width: 2,
                  background: 'var(--vn-border-default)',
                  marginTop: 4,
                }}
              />
            )}
          </div>

          {/* Action card */}
          <div style={{ flex: 1 }}>
            <ActionRow
              index={i}
              action={action}
              totalCount={actions.length}
              channels={channels}
              watchpoints={watchpoints}
              showErrors={showErrors}
              onChange={(updated) => updateAt(i, updated)}
              onMoveUp={() => moveUp(i)}
              onMoveDown={() => moveDown(i)}
              onRemove={() => removeAt(i)}
            />
          </div>
        </div>
      ))}

      {/* Validation error when no actions */}
      {showErrors && actions.length === 0 && (
        <p style={{ margin: 0, color: 'var(--vn-danger-text)', fontSize: 'var(--vn-font-size-sm)' }}>
          At least one action is required.
        </p>
      )}

      {/* Add action button */}
      <Button
        size="sm"
        variant="secondary"
        startIcon={<Plus size={14} />}
        onClick={addAction}
        style={{ alignSelf: 'flex-start' }}
      >
        Add action
      </Button>
    </div>
  );
}
